/**
 * Ring-buffer of the last N `WebInitCache` merge attempts, plus (optional)
 * full JSON snapshots for side-by-side diff. Populated by AuthProvider's
 * fetchInitCache. Read by the SuperAdmin Cache Browser tool.
 *
 * Deep capture (full snapshots) is off by default — InitCache can be
 * several MB and holding 20 copies would cost real memory. Flip it from
 * the tool only while actively debugging a merge issue.
 */
import type { InitCache, WebCacheTS } from "../types/session.ts";

const MAX_EVENTS = 20;
const DEEP_CAPTURE_KEY = "cacheDebug.deepCapture";

export interface CacheMergeEvent {
  id: string;
  at: number;
  /** What the client sent as TS on the request ("full" == TS: undefined). */
  requestTs: WebCacheTS | undefined | "full";
  /** Approx bytes of response.oData (JSON length, not wire bytes). */
  responseBytes: number;
  topLevelKeys: string[];
  /** Keys whose response value is a non-empty array or non-empty object. */
  nonEmptyKeys: string[];
  /** The TS the server returned inside oData. */
  responseTs: WebCacheTS | undefined;
  mergeOk: boolean;
  error?: string;
  // Populated only when deep capture is enabled
  prevSnapshot?: InitCache | null;
  responseSnapshot?: InitCache;
  mergedSnapshot?: InitCache | null;
}

// events is replaced-not-mutated so useSyncExternalStore sees a fresh reference
// on each change and skips re-render otherwise.
let events: readonly CacheMergeEvent[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

export function isDeepCaptureEnabled(): boolean {
  try {
    return sessionStorage.getItem(DEEP_CAPTURE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setDeepCaptureEnabled(v: boolean): void {
  try {
    sessionStorage.setItem(DEEP_CAPTURE_KEY, v ? "1" : "0");
  } catch {
    /* storage unavailable */
  }
  emit();
}

function emit() {
  for (const l of listeners) l();
}

export function subscribeCacheDebug(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getCacheMergeEvents(): readonly CacheMergeEvent[] {
  return events;
}

export function clearCacheMergeEvents(): void {
  events = [];
  emit();
}

function approxSize(x: unknown): number {
  try {
    return JSON.stringify(x).length;
  } catch {
    return 0;
  }
}

function computeNonEmptyKeys(x: unknown): string[] {
  if (!x || typeof x !== "object") return [];
  const out: string[] = [];
  for (const [k, v] of Object.entries(x as Record<string, unknown>)) {
    if (Array.isArray(v)) {
      if (v.length > 0) out.push(k);
    } else if (v && typeof v === "object") {
      if (Object.keys(v).length > 0) out.push(k);
    } else if (v !== undefined && v !== null && v !== "") {
      out.push(k);
    }
  }
  return out;
}

function cloneIfDeep<T>(x: T): T | undefined {
  if (!isDeepCaptureEnabled()) return undefined;
  try {
    return JSON.parse(JSON.stringify(x)) as T;
  } catch {
    return undefined;
  }
}

export function recordCacheMergeEvent(input: {
  requestTs: WebCacheTS | undefined | "full";
  prev: InitCache | null;
  response: InitCache | undefined;
  merged: InitCache | null;
  mergeOk: boolean;
  error?: string;
}): void {
  const { requestTs, prev, response, merged, mergeOk, error } = input;
  const topKeys =
    response && typeof response === "object"
      ? Object.keys(response as unknown as Record<string, unknown>)
      : [];
  const event: CacheMergeEvent = {
    id: `m${nextId++}`,
    at: Date.now(),
    requestTs,
    responseBytes: response ? approxSize(response) : 0,
    topLevelKeys: topKeys,
    nonEmptyKeys: response ? computeNonEmptyKeys(response) : [],
    responseTs: response?.TS,
    mergeOk,
    error,
    prevSnapshot: cloneIfDeep(prev),
    responseSnapshot: cloneIfDeep(response),
    mergedSnapshot: cloneIfDeep(merged),
  };
  const next = [event, ...events];
  events = next.length > MAX_EVENTS ? next.slice(0, MAX_EVENTS) : next;
  emit();
}

/**
 * Recursively clone a WebCacheTS replacing every numeric leaf with max(0, n-1).
 * Used by the Cache Browser "Decrement all TS" debug action to force the server
 * to treat every section as stale, so we can see what a fresh delta looks like.
 */
export function decrementAllTs(ts: WebCacheTS | undefined): WebCacheTS | undefined {
  if (!ts) return ts;
  return walkDecrement(ts) as WebCacheTS;
}

function walkDecrement(x: unknown): unknown {
  if (typeof x === "number") return Math.max(0, x - 1);
  if (Array.isArray(x)) return x.map(walkDecrement);
  if (x && typeof x === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(x as Record<string, unknown>)) {
      out[k] = walkDecrement(v);
    }
    return out;
  }
  return x;
}
